/**
 * Centralized cache for static API data (general + expiries)
 * Ensures only ONE API call per page refresh for each endpoint
 */

import api from "./api";
import { API_ENDPOINTS } from "../config/api.config";

// Global cache - shared across all components
let generalDataCache = null;
let expiriesDataCache = null;
let isGeneralLoading = false;
let isExpiriesLoading = false;
let generalLoadingPromise = null;
let expiriesLoadingPromise = null;

const expiryDateValue = (expiry) => {
  if (!expiry || typeof expiry !== "string") return Number.POSITIVE_INFINITY;

  const parts = expiry.split("-");
  if (parts.length !== 3) return Number.POSITIVE_INFINITY;

  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);
  if (!day || !month || !year) return Number.POSITIVE_INFINITY;

  return year * 10000 + month * 100 + day;
};

const todayISTValue = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(new Date());

  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return Number(byType.year) * 10000 + Number(byType.month) * 100 + Number(byType.day);
};

const nearestExpiryValue = (expiries) => {
  if (!Array.isArray(expiries) || expiries.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  const today = todayISTValue();
  return expiries.reduce((nearest, expiry) => {
    const value = expiryDateValue(expiry);
    if (value >= today && value < nearest) return value;
    return nearest;
  }, Number.POSITIVE_INFINITY);
};

export const sortIndicesByNearestExpiry = (indices, expiriesByIndex) => {
  if (!Array.isArray(indices) || indices.length === 0) return [];

  return [...indices].sort((left, right) => {
    const leftExpiry = nearestExpiryValue(expiriesByIndex?.[left]);
    const rightExpiry = nearestExpiryValue(expiriesByIndex?.[right]);

    if (leftExpiry !== rightExpiry) {
      return leftExpiry - rightExpiry;
    }

    return left.localeCompare(right);
  });
};

/**
 * Get general data (lot sizes, index info) with single API call guarantee
 * @returns {Promise<Object>} General data with lot_sizes and index_info
 */
export const getGeneralData = async () => {
  // Return cached data if available
  if (generalDataCache) {
    return generalDataCache;
  }

  // If already loading, return the existing promise
  if (isGeneralLoading && generalLoadingPromise) {
    return generalLoadingPromise;
  }

  // Create new loading promise
  isGeneralLoading = true;
  generalLoadingPromise = fetchGeneralDataOnce();

  try {
    const data = await generalLoadingPromise;
    return data;
  } finally {
    isGeneralLoading = false;
    generalLoadingPromise = null;
  }
};

/**
 * Internal function to fetch data once
 */
const fetchGeneralDataOnce = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.GENERAL);
    const responseData = response.data;

    // Parse the response format
    if (responseData && responseData.success && responseData.data) {
      generalDataCache = {
        indices: responseData.data.indices || {},
        sorted_indices: responseData.data.sorted_indices || [],
      };

      return generalDataCache;
    } else {
      throw new Error("Invalid response format");
    }
  } catch (error) {
    console.error("Error fetching general data:", error);

    // Do not cache fallback data. Lot sizes are trading-critical and must be
    // refetched from the API after login/session recovery.
    return {
      indices: {},
      sorted_indices: [],
    };
  }
};

/**
 * Get lot sizes only
 */
export const getLotSizes = async () => {
  const data = await getGeneralData();
  const lotSizes = {};
  Object.keys(data.indices).forEach((key) => {
    lotSizes[key] = data.indices[key].lot_size;
  });
  return lotSizes;
};

/**
 * Get freeze limits only
 */
export const getFreezeLimits = async () => {
  const data = await getGeneralData();
  const freezeLimits = {};
  Object.keys(data.indices).forEach((key) => {
    freezeLimits[key] = data.indices[key].freeze_limit;
  });
  return freezeLimits;
};

/**
 * Get index info only
 */
export const getIndexInfo = async () => {
  const data = await getGeneralData();
  const indexInfo = {};
  Object.keys(data.indices).forEach((key) => {
    const index = data.indices[key];
    indexInfo[key] = {
      index_number: index.index_number,
      display_name: key, // Use key as display name
      enabled: index.enabled,
    };
  });
  return indexInfo;
};

/**
 * Get expiries data with single API call guarantee
 * @returns {Promise<Object>} Expiries data indexed by instrument
 */
export const getExpiriesData = async () => {
  // Return cached data if available
  if (expiriesDataCache) {
    return expiriesDataCache;
  }

  // If already loading, return the existing promise
  if (isExpiriesLoading && expiriesLoadingPromise) {
    return expiriesLoadingPromise;
  }

  // Create new loading promise
  isExpiriesLoading = true;
  expiriesLoadingPromise = fetchExpiriesDataOnce();

  try {
    const data = await expiriesLoadingPromise;
    return data;
  } finally {
    isExpiriesLoading = false;
    expiriesLoadingPromise = null;
  }
};

/**
 * Internal function to fetch expiries data once
 */
const fetchExpiriesDataOnce = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.EXPIRIES);
    const responseData = response.data;

    // Parse the response format
    if (responseData && responseData.success && responseData.data) {
      expiriesDataCache = responseData.data;
      return expiriesDataCache;
    } else {
      throw new Error("Invalid expiries response format");
    }
  } catch (error) {
    console.error("❌ Error fetching expiries data:", error);

    // Return default/empty structure on error
    expiriesDataCache = {
      NIFTY: [],
      BANKNIFTY: [],
      SENSEX: [],
    };

    return expiriesDataCache;
  }
};

/**
 * Get expiries for a specific index
 * @param {string} index - Index name (NIFTY, BANKNIFTY, SENSEX)
 * @returns {Promise<Array>} Array of expiry dates for the index
 */
export const getExpiriesForIndex = async (index) => {
  const data = await getExpiriesData();
  return data[index] || [];
};

/**
 * Get all available indices from expiries data
 * @returns {Promise<Array>} Array of available index names
 */
export const getAvailableIndices = async () => {
  const data = await getExpiriesData();
  return Object.keys(data);
};

/**
 * Clear all caches (for testing or force refresh)
 */
export const clearAllCaches = () => {
  generalDataCache = null;
  expiriesDataCache = null;
  isGeneralLoading = false;
  isExpiriesLoading = false;
  generalLoadingPromise = null;
  expiriesLoadingPromise = null;
};

/**
 * Clear general data cache only (for backward compatibility)
 */
export const clearGeneralDataCache = () => {
  generalDataCache = null;
  isGeneralLoading = false;
  generalLoadingPromise = null;
};

/**
 * Clear expiries cache only
 */
export const clearExpiriesCache = () => {
  expiriesDataCache = null;
  isExpiriesLoading = false;
  expiriesLoadingPromise = null;
};
